"use client"

import React, { useEffect, useState } from "react"
import { normalizePhoneExtension } from "@/utils/phone"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { getExampleNumber } from "libphonenumber-js"
import examples from "libphonenumber-js/mobile/examples"
import { CheckIcon } from "lucide-react"
import RPNInput, { getCountryCallingCode, type Country, type FlagProps, type Value } from "react-phone-number-input"
import flags from "react-phone-number-input/flags"

import { FALLBACK_DEFAULT_COUNTRY, useCompanyDefaultCountry } from "@/hooks/use-company-default-country"

import { cn } from "./utils"

export type { Country }

type PhoneInputSize = "default" | "compact"

const PhoneInputSizeContext = React.createContext<PhoneInputSize>("default")
const PinnedCountryContext = React.createContext<Country>("US")

function examplePlaceholder(country: Country): string {
  const example = getExampleNumber(country, examples)
  return example?.formatNational() ?? ""
}

export interface PhoneInputProps {
  value: string
  onChange: (value: string) => void
  defaultCountry?: Country
  placeholder?: string
  disabled?: boolean
  className?: string
  extension?: string
  onExtensionChange?: (value: string) => void
  size?: PhoneInputSize
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      defaultCountry,
      placeholder,
      disabled,
      className,
      extension,
      onExtensionChange,
      size = "default",
    },
    ref
  ) => {
    const companyDefaultCountry = useCompanyDefaultCountry()
    const resolvedDefaultCountry = defaultCountry ?? companyDefaultCountry ?? FALLBACK_DEFAULT_COUNTRY
    const [country, setCountry] = useState<Country>(resolvedDefaultCountry)
    const [inputKey, setInputKey] = useState(resolvedDefaultCountry)
    const hasExtensionInput = !!onExtensionChange
    const isCompact = size === "compact"

    useEffect(() => {
      if (value) return
      setCountry(resolvedDefaultCountry)
      setInputKey(resolvedDefaultCountry)
    }, [resolvedDefaultCountry, value])

    const phoneInput = (
      <RPNInput
        ref={ref as never}
        key={inputKey}
        className={cn("flex", hasExtensionInput ? "min-w-0 flex-1" : className)}
        international={false}
        defaultCountry={resolvedDefaultCountry}
        initialValueFormat="national"
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={hasExtensionInput ? InputComponentWithExtension : InputComponent}
        smartCaret={false}
        value={(value || undefined) as Value}
        onChange={(val) => onChange(val || "")}
        onCountryChange={(c) => {
          if (c) setCountry(c)
        }}
        placeholder={placeholder ?? examplePlaceholder(country)}
        disabled={disabled}
      />
    )

    if (!hasExtensionInput) {
      return (
        <PhoneInputSizeContext.Provider value={size}>
          <PinnedCountryContext.Provider value={resolvedDefaultCountry}>{phoneInput}</PinnedCountryContext.Provider>
        </PhoneInputSizeContext.Provider>
      )
    }

    return (
      <PhoneInputSizeContext.Provider value={size}>
        <PinnedCountryContext.Provider value={resolvedDefaultCountry}>
          <div className={cn("flex", className)}>
            {phoneInput}
            <Input
              className={cn(
                "focus-visible:border-ring shrink-0 rounded-l-none rounded-r-md",
                isCompact ? "h-7 w-12 px-1.5 text-sm md:text-sm" : "h-9 w-16 px-2"
              )}
              disabled={disabled}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="ext"
              value={extension ?? ""}
              onChange={(event) => onExtensionChange(normalizePhoneExtension(event.target.value))}
            />
          </div>
        </PinnedCountryContext.Provider>
      </PhoneInputSizeContext.Provider>
    )
  }
)
PhoneInput.displayName = "PhoneInput"

const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, ...props }, ref) => {
    const size = React.useContext(PhoneInputSizeContext)

    return (
      <Input
        className={cn("rounded-l-none rounded-r-md", size === "compact" && "h-7 px-2 text-sm md:text-sm", className)}
        {...props}
        ref={ref}
      />
    )
  }
)
InputComponent.displayName = "InputComponent"

const InputComponentWithExtension = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, ...props }, ref) => {
    const size = React.useContext(PhoneInputSizeContext)

    return (
      <Input
        className={cn(
          "rounded-l-none rounded-r-none border-r-0",
          size === "compact" && "h-7 px-2 text-sm md:text-sm",
          className
        )}
        {...props}
        ref={ref}
      />
    )
  }
)
InputComponentWithExtension.displayName = "InputComponentWithExtension"

type CountryEntry = { label: string; value: Country | undefined }

type CountrySelectProps = {
  disabled?: boolean
  value?: Country
  options: CountryEntry[]
  onChange: (country: Country) => void
}

const CountrySelect = ({ disabled, value: selectedCountry, options: countryList, onChange }: CountrySelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const size = React.useContext(PhoneInputSizeContext)
  const pinnedCountry = React.useContext(PinnedCountryContext)

  return (
    <Popover open={isOpen} modal onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "border-input focus-visible:border-ring bg-tinted hover:bg-accent active:bg-accent/80 flex cursor-pointer items-center rounded-l-md border border-r-0 shadow-xs transition-colors focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
            size === "compact" ? "h-7 gap-0.5 px-2" : "h-9 gap-1 px-3"
          )}
          disabled={disabled}
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry ?? ""} />
          {selectedCountry && selectedCountry !== "US" && (
            <span className={cn("text-muted-foreground", size === "compact" ? "text-[11px]" : "text-xs")}>
              +{getCountryCallingCode(selectedCountry)}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" onCloseAutoFocus={(e) => e.preventDefault()}>
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {[...countryList]
                .sort((a, b) => {
                  const rank = (code: Country | undefined) => {
                    if (code === pinnedCountry) return 0
                    if (code === "US") return 1
                    return 2
                  }
                  return rank(a.value) - rank(b.value)
                })
                .map(({ value, label }) =>
                  value ? (
                    <CommandItem
                      key={value}
                      className="gap-2"
                      onSelect={() => {
                        onChange(value)
                        setIsOpen(false)
                      }}
                    >
                      <FlagComponent country={value} countryName={label} />
                      <span className="flex-1 text-sm">{label}</span>
                      <span className="text-foreground/50 text-sm">{`+${getCountryCallingCode(value)}`}</span>
                      <CheckIcon
                        className={cn("ml-auto size-4", value === selectedCountry ? "opacity-100" : "opacity-0")}
                      />
                    </CommandItem>
                  ) : null
                )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const FlagComponent = ({ country, countryName }: Omit<FlagProps, "country"> & { country?: Country }) => {
  const Flag = country ? flags[country] : undefined
  const size = React.useContext(PhoneInputSizeContext)

  return (
    <span
      className={cn(
        "bg-foreground/20 flex overflow-hidden [&_svg:not([class*='size-'])]:size-full",
        size === "compact" ? "h-3 w-5 rounded-[2px]" : "h-4 w-6 rounded-sm"
      )}
    >
      {Flag && <Flag title={countryName} />}
    </span>
  )
}
